import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Gavel as LegalIcon,
  Business as BusinessIcon,
  GetApp as DownloadIcon,
  Visibility as ExamineIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';

const searchSteps = [
  {
    step: 1,
    title: 'Initial Property Identification',
    description: 'Verify legal description, block and lot numbers, and current ownership.',
    details: [
      'Confirm property address matches legal description',
      'Verify block and lot numbers with tax records',
      'Check current deed on record',
      'Identify any recent transfers or changes',
    ],
    timeframe: '30 minutes',
    critical: true,
  },
  {
    step: 2,
    title: 'Chain of Title Review',
    description: 'Examine ownership history for required period (typically 60 years in NJ).',
    details: [
      'Start with current owner and work backwards',
      'Verify each transfer is properly recorded',
      'Check for any gaps in ownership',
      'Identify any irregular conveyances',
    ],
    timeframe: '2-3 hours',
    critical: true,
  },
  {
    step: 3,
    title: 'Lien and Encumbrance Search',
    description: 'Search for all liens, judgments, and encumbrances affecting the property.',
    details: [
      'Check federal tax liens (IRS)',
      'Search state and local tax liens',
      'Review judgment records',
      'Examine mechanic\'s lien filings',
      'Check for UCC filings on fixtures',
    ],
    timeframe: '1-2 hours',
    critical: true,
  },
  {
    step: 4,
    title: 'Easement and Restriction Review',
    description: 'Identify all easements, covenants, and restrictions affecting the property.',
    details: [
      'Review recorded easements',
      'Check subdivision restrictions',
      'Examine utility easements',
      'Identify access rights',
    ],
    timeframe: '1 hour',
    critical: false,
  },
  {
    step: 5,
    title: 'Municipal Records Check',
    description: 'Verify compliance with local ordinances and identify municipal liens.',
    details: [
      'Check building permit compliance',
      'Review zoning compliance',
      'Search for municipal liens',
      'Verify certificate of occupancy',
    ],
    timeframe: '1-2 hours',
    critical: true,
  },
];

const commonIssues = [
  {
    issue: 'Missing Mortgages',
    description: 'Mortgage recorded but no satisfaction found',
    severity: 'High',
    resolution: 'Contact lender for satisfaction or payoff letter',
    prevention: 'Always verify mortgage satisfactions in chain',
  },
  {
    issue: 'Judgment Liens',
    description: 'Unsatisfied judgments against prior owners',
    severity: 'High',
    resolution: 'Obtain satisfaction or negotiate payoff at closing',
    prevention: 'Search judgment records for all parties in chain',
  },
  {
    issue: 'Estate Issues',
    description: 'Property passed through estate without proper administration',
    severity: 'High',
    resolution: 'May require estate administration or affidavit of heirship',
    prevention: 'Carefully examine all death certificates and estate documents',
  },
  {
    issue: 'Name Variations',
    description: 'Same person using different name variations',
    severity: 'Medium',
    resolution: 'Obtain affidavit of identity or name change documentation',
    prevention: 'Search all reasonable name variations',
  },
  {
    issue: 'Mechanic\'s Liens',
    description: 'Unpaid contractor or supplier claims',
    severity: 'Medium',
    resolution: 'Obtain lien waivers or negotiate payment',
    prevention: 'Check for recent improvements and verify all work is paid',
  },
  {
    issue: 'Municipal Violations',
    description: 'Outstanding code violations or unpaid municipal charges',
    severity: 'Medium',
    resolution: 'Obtain municipal certificate or arrange payment',
    prevention: 'Always check with local municipality for open violations',
  },
];

const njSpecificRequirements = [
  {
    requirement: 'Affidavit of Title',
    description: 'Required from seller at closing in most transactions',
    details: 'Confirms no unreported liens, judgments, or other encumbrances',
  },
  {
    requirement: 'Property Disclosure',
    description: 'Seller must provide condition disclosure form',
    details: 'Review for any disclosed issues that might affect title',
  },
  {
    requirement: 'Municipal Certificate',
    description: 'Many municipalities require certificate of compliance',
    details: 'Confirms no outstanding violations or unpaid fees',
  },
  {
    requirement: 'Water/Sewer Certificate',
    description: 'Certificate showing water/sewer accounts current',
    details: 'Required in most municipalities for closing',
  },
];

const TitleSearchManual: React.FC = () => {
  const [expandedStep, setExpandedStep] = useState<string | false>(false);

  const handleStepChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedStep(isExpanded ? panel : false);
  };

  return (
    <>
      <Helmet>
        <title>Title Search Manual | MAD Title Agency - Professional Guide for NJ Title Examination</title>
        <meta
          name="description"
          content="Comprehensive title search manual for New Jersey professionals. Step-by-step examination procedures, common issues, and best practices for title professionals."
        />
        <meta
          name="keywords"
          content="title search manual, NJ title examination, title professional training, chain of title, lien search, title defects, New Jersey title law"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Title Search Manual
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#059669', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Manual"
                sx={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  mb: 2,
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                New Jersey Title Search Manual
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Comprehensive guide for professional title examination and analysis
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#f59e0b',
                  '&:hover': { backgroundColor: '#d97706' },
                }}
              >
                Download Complete Manual
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <SearchIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Professional Standard
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  NJ Best Practices
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#059669' }}>
            Introduction to Title Searching in New Jersey
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Title searching is the foundation of title insurance and safe real estate transactions. 
                This manual provides comprehensive guidance for conducting thorough title examinations 
                in New Jersey, following industry best practices and state-specific requirements.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                New Jersey's recording system, legal requirements, and local practices create unique 
                considerations for title professionals. This manual addresses these specifics while 
                providing a systematic approach to title examination.
              </Typography>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> This manual is for professional title examiners and 
                  should be used in conjunction with proper training and supervision. Title examination 
                  requires extensive knowledge of real estate law and local practices.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#f0fdfa' }}>
                <ExamineIcon sx={{ fontSize: 48, color: '#059669', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Search Standards
                </Typography>
                <Typography variant="body2">
                  • 60-year chain minimum<br />
                  • Comprehensive lien search<br />
                  • Municipal compliance check<br />
                  • Complete documentation
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Search Process Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#059669' }}>
            Title Search Process
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Follow these steps systematically for every title examination:
          </Typography>
          
          {searchSteps.map((step, index) => (
            <Accordion
              key={index}
              expanded={expandedStep === `step${index}`}
              onChange={handleStepChange(`step${index}`)}
              sx={{ mb: 2, border: '1px solid #e2e8f0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: step.critical ? '#dc2626' : '#059669',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      mr: 3,
                    }}
                  >
                    {step.step}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {step.critical && (
                      <Chip
                        label="Critical"
                        size="small"
                        color="error"
                      />
                    )}
                    <Chip
                      label={step.timeframe}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 7 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Detailed Procedures:
                  </Typography>
                  <List>
                    {step.details.map((detail, detailIndex) => (
                      <ListItem key={detailIndex}>
                        <ListItemIcon>
                          <CheckIcon sx={{ color: '#059669' }} />
                        </ListItemIcon>
                        <ListItemText primary={detail} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Common Issues */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#059669' }}>
            Common Title Issues & Resolutions
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            Learn to identify and resolve these frequently encountered title problems:
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell><strong>Issue</strong></TableCell>
                  <TableCell><strong>Severity</strong></TableCell>
                  <TableCell><strong>Resolution</strong></TableCell>
                  <TableCell><strong>Prevention</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commonIssues.map((issue, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {issue.issue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {issue.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={issue.severity}
                        size="small"
                        color={issue.severity === 'High' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {issue.resolution}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {issue.prevention}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* NJ Specific Requirements */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#059669' }}>
            New Jersey Specific Requirements
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            These requirements are specific to New Jersey real estate transactions:
          </Typography>

          <Grid container spacing={4}>
            {njSpecificRequirements.map((req, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LegalIcon sx={{ color: '#f59e0b', mr: 2 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {req.requirement}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {req.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {req.details}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Best Practices */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#059669' }}>
            Best Practices & Quality Control
          </Typography>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#f0fdfa' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Documentation Standards
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#059669' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Maintain detailed search notes"
                      secondary="Document every step of the examination process"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#059669' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Copy all relevant documents"
                      secondary="Maintain complete file for future reference"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon sx={{ color: '#059669' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Use standardized forms"
                      secondary="Consistent reporting reduces errors"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, height: '100%', backgroundColor: '#fef3c7' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Quality Assurance
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Double-check all dates"
                      secondary="Date errors are common source of problems"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Verify all name spellings"
                      secondary="Name variations can hide title issues"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Review by senior examiner"
                      secondary="Independent review catches missed issues"
                    />
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="Title Search Manual for Professionals"
          resourceType="professional"
          formType="consultation"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f0fdfa', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Professional Training & Support
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              Enhance your title examination skills with our professional development programs.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Professional Services
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  borderColor: '#059669',
                  color: '#059669',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Get Training Referrals
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TitleSearchManual; 